import { useEffect, useRef } from 'react';

const TabAttention = ({ defaultFaviconUrl = '/logo.ico', notifFaviconUrl = '/logo_notif.ico', flashMessage = 'Finish your trip!' }) => {
  // Use refs to persist values across renders
  const focusTimeout = useRef(null);
  const attentionInterval = useRef(null);
  const originalTitle = useRef(document.title);
  const isAnimating = useRef(false);
  const clickCount = useRef(0);
  const isShowingFlashMessage = useRef(false);

  // Handle when an external link is clicked
  const handleExternalLink = () => {
    // Increment click counter to track new clicks
    clickCount.current++;
    
    // Reset and start fresh animation for each new click
    stopAnimation();
    
    // Start animation immediately
    startAnimation();
    
    // Also set up a timeout in case the user doesn't navigate away
    if (focusTimeout.current) clearTimeout(focusTimeout.current);
    focusTimeout.current = setTimeout(() => {
      // If we're still on the same page after 1 minute, stop the animation
      if (document.title === originalTitle.current) {
        stopAnimation();
      }
    }, 60000); // 1-minute delay
  };

  // Start the animation
  const startAnimation = () => {
    isAnimating.current = true;
    isShowingFlashMessage.current = false;
    if (attentionInterval.current) clearInterval(attentionInterval.current);
    attentionInterval.current = setInterval(() => flashTitle(flashMessage), 2000);
    toggleFaviconNotification(notifFaviconUrl);
  };

  // Stop the animation
  const stopAnimation = () => {
    isAnimating.current = false;
    isShowingFlashMessage.current = false;
    if (attentionInterval.current) clearInterval(attentionInterval.current);
    if (focusTimeout.current) clearTimeout(focusTimeout.current);
    document.title = originalTitle.current;
    toggleFaviconNotification(defaultFaviconUrl);
  };

  // Handle when focus returns (tab becomes visible again)
  const handleFocusReturned = () => {
    // Reset click counter when user returns to tab
    clickCount.current = 0;
    stopAnimation();
  };

  // Flash the title
  const flashTitle = (alternateTitle) => {
    if (isShowingFlashMessage.current) {
      document.title = originalTitle.current;
      isShowingFlashMessage.current = false;
    } else {
      document.title = alternateTitle;
      isShowingFlashMessage.current = true;
    }
  };

  // Toggle favicon
  const toggleFaviconNotification = (faviconUrl) => {
    const favicon = document.querySelector("link[rel='icon']");
    if (!favicon) return;
    favicon.href = faviconUrl;
  };

  // Detect tab focus changes and page unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocusReturned();
      } else if (document.visibilityState === 'hidden') {
        // Tab is hidden, start animation if not already running
        if (!isAnimating.current) {
          startAnimation();
        }
      }
    };

    const handleBeforeUnload = () => {
      // Page is about to unload, start animation
      startAnimation();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Update originalTitle on mount
    originalTitle.current = document.title;

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      stopAnimation();
    };
  }, [defaultFaviconUrl, notifFaviconUrl, flashMessage]); // Dependencies

  return { handleExternalLink };
};

export default TabAttention;